import React from "react"

// ——————————— Types ———————————
export type IncludedItem = {
  image: string
  name: string
  onEdit?: () => void
  editLabel?: string
}

export type CustomCardProps = {
  image: string
  price?: string
  alt?: string
  title: string
  description: string
  subDescription?: string
  includedItems: IncludedItem[]
  addToCartText?: string
  onAddToCart?: () => void
  className?: string
  link?: string // optional navigation link
}

// ——————————— Component ———————————
const CustomCardComponent = ({
  image,
  title,
  price,
  alt,
  description,
  subDescription = "INCLUDES ANY 2 SOULMATES:",
  includedItems,
  addToCartText = "Order Now",
  onAddToCart,
  className = "",
  link,
}: CustomCardProps) => {
  return (
    <div
      className={
        "relative mx-auto w-full max-w-[440px] rounded-2xl border border-neutral-800 bg-black p-2.5 shadow-2xl " +
        "[box-shadow:0_0_0_1px_rgba(255,255,255,0.06)_inset] flex flex-col gap-1.5 " +
        className
      }
    >
      {/* Top image with floating title pill */}
      <div className="relative overflow-hidden rounded-xl bg-neutral-900">
        <div className="aspect-[5/6] w-full flex items-center justify-center bg-neutral-900">
          <img
            src={image}
            alt={alt || title}
            className="max-h-full max-w-full object-contain p-2"
          />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />

        {/* Repositioned title pill: inside image near bottom */}
        <div className="absolute bottom-1.5 left-1/2 w-[min(92%,340px)] -translate-x-1/2 transition-all duration-500 ease-out">
          <div className="mx-auto inline-flex max-w-full items-center justify-center rounded-full bg-neutral-800/85 backdrop-blur-sm px-3 py-1.5 shadow-lg ring-1 ring-white/10">
            <span className="text-center text-[9.5px] font-semibold tracking-wide text-white sm:text-[10px]">
              {title}
            </span>
          </div>
        </div>
      </div>

      {/* Body copy (reduce top padding since title pill moved) */}
      <div className="px-2 pt-2 md:px-3">
        <p className="whitespace-pre-line text-[12px] leading-relaxed tracking-[0.04em] text-neutral-200 md:text-[12.5px]">
          {description}
        </p>
      </div>

      {price && <p className="text-center font-bold text-amber-300">{price.startsWith("$") ? price : `$${price}`}</p>}
      {/* Sub-heading */}
      {subDescription ? (
        <div className="px-2 pt-1 md:px-3">
          <p className="text-center text-[9px] font-extrabold tracking-[0.2em] text-neutral-300 md:text-[9.5px]">
            {subDescription}
          </p>
        </div>
      ) : null}

      {/* Included items grid (enlarged) */}
      <div className="grid grid-cols-2 gap-3.5 px-2 pt-2 md:px-4">
        {includedItems.map((item, idx) => (
          <div
            key={idx}
            className="group relative overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950/60 p-2 shadow-md ring-1 ring-white/5"
          >
            <div className="relative w-full overflow-hidden rounded-lg bg-neutral-900 flex items-center justify-center h-[130px] sm:h-[145px] md:h-[160px]">
              <img
                src={item.image}
                alt={item.name}
                className="h-full w-full object-contain p-2"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-[94%] flex items-center justify-center">
                <span className="text-center text-[7.5px] leading-snug font-extrabold tracking-[0.14em] text-neutral-200 md:text-[8px] bg-neutral-800/85 backdrop-blur-sm rounded-md px-1.5 py-[3px] shadow ring-1 ring-white/10 break-words whitespace-normal">
                  {item.name}
                </span>
              </div>
            </div>
            {item.onEdit ? (
              <button
                onClick={item.onEdit}
                className="absolute right-2 top-2 rounded-md bg-amber-500 px-2.5 py-1 text-[10px] font-bold text-black shadow hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                {item.editLabel ?? "EDIT"}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {/* Add to Cart CTA */}
      <div className="px-3 pb-1 pt-1">
        <button
          onClick={() => {
            if (link) {
              // simple navigation; could be replaced with router navigation if available
              window.location.href = link
              return
            }
            onAddToCart?.()
          }}
          className="mx-auto flex w-full max-w-[170px] items-center justify-center rounded-xl bg-amber-500 px-4 py-2 text-[9.5px] font-extrabold uppercase tracking-[0.15em] text-black shadow-md transition hover:translate-y-[-1px] hover:bg-amber-400 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
        >
          {addToCartText}
        </button>
      </div>
    </div>
  )
};

export default CustomCardComponent;
